// Client Census Report (built 9/3/2026): every client across Infiniti facilities -
// name, DOB, admit, discharge, insurance, policy #. TCIO episodes + Dazos alumni
// (admitted-only), grouped by facility, optional admit-date range, CSV export.
import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = 'https://tcio-api.onrender.com';
const fmtD = v => v ? new Date(String(v).slice(0, 10) + 'T00:00:00').toLocaleDateString('en-US') : '\u2014';

export default function ClientCensusReport({ user }) {
  const [facs, setFacs] = useState([]);
  const [facilityId, setFacilityId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [rows, setRows] = useState(null);
  const [meta, setMeta] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const canRun = ['ceo', 'admin', 'billing', 'ur_supervisor', 'facility_owner'].includes((user && user.role) || '');

  useEffect(() => {
    axios.get(`${API}/api/facilities`)
      .then(r => setFacs((r.data.data || []).filter(f => !/sandbox/i.test(f.name || f.org_name || ''))))
      .catch(() => setFacs([]));
  }, []);

  if (!canRun) return null;

  const run = async () => {
    setBusy(true); setErr(''); setRows(null);
    try {
      const p = new URLSearchParams();
      if (facilityId) p.set('facilityId', facilityId);
      if (dateFrom) p.set('dateFrom', dateFrom);
      if (dateTo) p.set('dateTo', dateTo);
      const r = await axios.get(`${API}/api/reports/client-census?` + p.toString());
      setRows(r.data.rows || []);
      setMeta({ tcio: r.data.tcio, alumni: r.data.alumni, count: r.data.count });
    } catch (e) {
      setErr((e.response && e.response.data && e.response.data.error) || 'Report failed');
    }
    setBusy(false);
  };

  const downloadCsv = () => {
    if (!rows || !rows.length) return;
    const esc = v => {
      const s2 = v === null || v === undefined ? '' : String(v);
      return /[",\n]/.test(s2) ? '"' + s2.replace(/"/g, '""') + '"' : s2;
    };
    const header = ['Facility', 'Last Name', 'First Name', 'DOB', 'Admit Date', 'Discharge Date', 'Insurance', 'Policy #', 'Admissions Rep', 'Lead Source', 'Source'];
    const lines = [header.join(',')].concat(rows.map(r => [
      r.facility, r.last_name, r.first_name,
      r.dob ? String(r.dob).slice(0, 10) : '',
      r.admit_date ? String(r.admit_date).slice(0, 10) : '',
      r.discharge_date ? String(r.discharge_date).slice(0, 10) : '',
      r.insurance || '', r.policy_number || '', r.admissions_rep || '', r.lead_source || '', r.source
    ].map(esc).join(',')));
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    const range = (dateFrom || dateTo) ? `${dateFrom || 'start'}_to_${dateTo || 'now'}` : 'all_time';
    a.download = `TCIO_Client_Census_${range}_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 4000);
  };

  const byFac = {};
  (rows || []).forEach(r => { (byFac[r.facility] = byFac[r.facility] || []).push(r); });

  const inp = { padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13 };
  const th = { textAlign: 'left', fontSize: 10.5, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, padding: '8px 10px', borderBottom: '1px solid #e5e7eb', background: '#f8fafc' };
  const td = { fontSize: 13, padding: '7px 10px', borderBottom: '1px solid #f1f5f9', color: '#111827' };

  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 22, marginTop: 20 }}>
      <div style={{ fontSize: 17, fontWeight: 700, color: '#111827' }}>Client Census Report</div>
      <div style={{ fontSize: 12.5, color: '#6b7280', marginTop: 2, marginBottom: 14 }}>
        Every client across Infiniti facilities - TCIO records plus imported alumni (admitted clients only). Leave dates blank for all time.</div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
        <select style={inp} value={facilityId} onChange={e => setFacilityId(e.target.value)}>
          <option value="">All Facilities</option>
          {facs.map(f => <option key={f.id} value={f.id}>{f.name || f.org_name}</option>)}
        </select>
        <input type="date" style={inp} value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
        <span style={{ fontSize: 12.5, color: '#6b7280' }}>to</span>
        <input type="date" style={inp} value={dateTo} onChange={e => setDateTo(e.target.value)} />
        <button onClick={run} disabled={busy}
          style={{ background: '#4479B0', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
          {busy ? 'Running\u2026' : 'Run Report'}</button>
        {rows && rows.length > 0 &&
          <button onClick={downloadCsv}
            style={{ background: '#fff', color: '#4479B0', border: '1.5px solid #D8E3EE', borderRadius: 8, padding: '8px 15px', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
            Download CSV ({rows.length})</button>}
      </div>
      {err && <div style={{ color: '#b91c1c', fontSize: 13, marginBottom: 10 }}>{err}</div>}
      {meta &&
        <div style={{ fontSize: 12.5, color: '#374151', marginBottom: 12 }}>
          {meta.count} clients total {'\u00b7'} {meta.tcio} TCIO {'\u00b7'} {meta.alumni} alumni</div>}
      {rows && rows.length === 0 &&
        <div style={{ fontSize: 13.5, color: '#6b7280', padding: 14 }}>No clients match this range.</div>}
      {Object.keys(byFac).map(fac => (
        <div key={fac} style={{ marginBottom: 22 }}>
          <div style={{ fontSize: 14.5, fontWeight: 700, color: '#111827', margin: '10px 0 6px' }}>
            {fac} <span style={{ fontWeight: 400, color: '#6b7280', fontSize: 12.5 }}>{'\u00b7'} {byFac[fac].length} clients</span></div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr>
                <th style={th}>Last Name</th><th style={th}>First Name</th><th style={th}>DOB</th>
                <th style={th}>Admit</th><th style={th}>Discharge</th><th style={th}>Insurance</th>
                <th style={th}>Policy #</th><th style={th}>Admissions Rep</th><th style={th}>Lead Source</th><th style={th}>Source</th></tr></thead>
              <tbody>
                {byFac[fac].map((r, i) => (
                  <tr key={i}>
                    <td style={td}>{r.last_name}</td><td style={td}>{r.first_name}</td>
                    <td style={td}>{fmtD(r.dob)}</td><td style={td}>{fmtD(r.admit_date)}</td>
                    <td style={td}>{fmtD(r.discharge_date)}</td>
                    <td style={td}>{r.insurance || '\u2014'}</td>
                    <td style={{ ...td, fontFamily: 'monospace', fontSize: 12 }}>{r.policy_number || '\u2014'}</td>
                    <td style={td}>{r.admissions_rep || '\u2014'}</td>
                    <td style={td}>{r.lead_source || '\u2014'}</td>
                    <td style={{ ...td, color: r.source === 'Alumni' ? '#7C5CE7' : '#4479B0', fontWeight: 600, fontSize: 12 }}>{r.source}</td>
                  </tr>))}
              </tbody>
            </table>
          </div>
        </div>))}
    </div>
  );
}
